import { ReactNode } from 'react';
import {
  ActivityIndicator,
  Pressable,
  StyleProp,
  StyleSheet,
  Text,
  View,
  ViewStyle,
} from 'react-native';
import { Check, ChevronRight, Minus, Plus } from 'lucide-react-native';

import { Fonts, Palette, Radius, Spacing } from '@/constants/theme';

export function PrimaryCTA({
  label,
  onPress,
  disabled,
  loading,
  style,
}: {
  label: string;
  onPress: () => void;
  disabled?: boolean;
  loading?: boolean;
  style?: StyleProp<ViewStyle>;
}) {
  return (
    <Pressable
      accessibilityRole="button"
      onPress={onPress}
      disabled={disabled || loading}
      style={({ pressed }) => [
        styles.cta,
        styles.ctaPrimary,
        disabled && styles.ctaDisabled,
        pressed && !disabled && styles.ctaPressed,
        style,
      ]}>
      {loading ? (
        <ActivityIndicator color={Palette.cream} />
      ) : (
        <>
          <Text style={[styles.ctaText, { color: Palette.cream }]}>{label}</Text>
          <ChevronRight color={Palette.cream} size={18} strokeWidth={2.2} />
        </>
      )}
    </Pressable>
  );
}

export function HoneyCTA({
  label,
  onPress,
  disabled,
  loading,
  style,
}: {
  label: string;
  onPress: () => void;
  disabled?: boolean;
  loading?: boolean;
  style?: StyleProp<ViewStyle>;
}) {
  return (
    <Pressable
      accessibilityRole="button"
      onPress={onPress}
      disabled={disabled || loading}
      style={({ pressed }) => [
        styles.cta,
        styles.ctaHoney,
        disabled && styles.ctaDisabled,
        pressed && !disabled && styles.ctaPressed,
        style,
      ]}>
      {loading ? (
        <ActivityIndicator color="#3A2A0C" />
      ) : (
        <>
          <Text style={[styles.ctaText, { color: '#3A2A0C' }]}>{label}</Text>
          <ChevronRight color="#3A2A0C" size={18} strokeWidth={2.2} />
        </>
      )}
    </Pressable>
  );
}

export function SkipLink({ label, onPress }: { label: string; onPress: () => void }) {
  return (
    <Pressable onPress={onPress} accessibilityRole="link">
      <Text style={styles.skip}>{label}</Text>
    </Pressable>
  );
}

export function OptionRow({
  title,
  subtitle,
  badge,
  selected,
  icon,
  onPress,
}: {
  title: string;
  subtitle?: string;
  badge?: string;
  selected: boolean;
  icon?: ReactNode;
  onPress: () => void;
}) {
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityState={{ selected }}
      onPress={onPress}
      style={({ pressed }) => [
        styles.opt,
        selected && styles.optSel,
        pressed && styles.optPressed,
      ]}>
      {icon ? (
        <View style={[styles.optIco, selected && styles.optIcoSel]}>{icon}</View>
      ) : null}
      <View style={styles.optBody}>
        {badge ? (
          <View style={styles.badge}>
            <Text style={styles.badgeText}>{badge.toUpperCase()}</Text>
          </View>
        ) : null}
        <Text style={styles.optTitle}>{title}</Text>
        {subtitle ? <Text style={styles.optSub}>{subtitle}</Text> : null}
      </View>
      <View style={[styles.optCheck, selected && styles.optCheckSel]}>
        {selected ? <Check color={Palette.cream} size={14} strokeWidth={2.4} /> : null}
      </View>
    </Pressable>
  );
}

/**
 * Tile with optional quantity stepper. If `count` is provided, the tile
 * becomes qty-aware: tapping the body toggles 0 ↔ 1, and a +/- stepper
 * appears once selected so users can record 2 or 3 instances (multiple
 * fireplaces, mowers, ceiling fans, etc.).
 */
export function Tile({
  label,
  selected,
  icon,
  onPress,
  count,
  onIncrement,
  onDecrement,
}: {
  label: string;
  selected: boolean;
  icon: ReactNode;
  onPress: () => void;
  count?: number;
  onIncrement?: () => void;
  onDecrement?: () => void;
}) {
  const showStepper =
    typeof count === 'number' && selected && onIncrement && onDecrement;

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityState={{ selected }}
      onPress={onPress}
      style={({ pressed }) => [
        styles.tile,
        selected && styles.tileSel,
        pressed && styles.tilePressed,
      ]}>
      <View style={[styles.tileIco, selected && styles.tileIcoSel]}>{icon}</View>
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text style={styles.tileLabel}>{label}</Text>
        {typeof count === 'number' && count > 1 ? (
          <Text style={styles.tileCount}>×{count}</Text>
        ) : null}
      </View>
      {showStepper ? (
        <View style={styles.stepper}>
          <Pressable
            accessibilityLabel={`Remove one ${label}`}
            onPress={onDecrement}
            hitSlop={6}
            style={({ pressed }) => [styles.stepBtn, pressed && { opacity: 0.7 }]}>
            <Minus size={12} color={Palette.forest} strokeWidth={2.2} />
          </Pressable>
          <Pressable
            accessibilityLabel={`Add another ${label}`}
            onPress={onIncrement}
            hitSlop={6}
            style={({ pressed }) => [styles.stepBtn, pressed && { opacity: 0.7 }]}>
            <Plus size={12} color={Palette.forest} strokeWidth={2.2} />
          </Pressable>
        </View>
      ) : null}
    </Pressable>
  );
}

const styles = StyleSheet.create({
  cta: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: Spacing.two,
    paddingVertical: Spacing.four,
    borderRadius: Radius.lg,
  },
  ctaPrimary: {
    backgroundColor: Palette.forest,
  },
  ctaHoney: {
    backgroundColor: Palette.honey,
  },
  ctaDisabled: {
    opacity: 0.4,
  },
  ctaPressed: {
    opacity: 0.94,
    transform: [{ scale: 0.985 }],
  },
  ctaText: {
    fontFamily: Fonts.sansBold,
    fontSize: 16,
    letterSpacing: 0.1,
  },
  skip: {
    marginTop: Spacing.three,
    textAlign: 'center',
    fontSize: 13.5,
    fontFamily: Fonts.sansSemibold,
    color: Palette.inkFaint,
  },

  // Option rows (single-select cards)
  opt: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.three,
    backgroundColor: Palette.card,
    borderWidth: 1.5,
    borderColor: Palette.line,
    borderRadius: Radius.lg,
    padding: Spacing.four,
  },
  optSel: {
    borderColor: Palette.forest,
    backgroundColor: '#EEF3EE',
  },
  optPressed: {
    transform: [{ scale: 0.985 }],
  },
  optIco: {
    width: 44,
    height: 44,
    borderRadius: 13,
    backgroundColor: '#EAF0EA',
    alignItems: 'center',
    justifyContent: 'center',
  },
  optIcoSel: {
    backgroundColor: Palette.forest,
  },
  optBody: {
    flex: 1,
  },
  optTitle: {
    fontSize: 15,
    fontFamily: Fonts.sansBold,
    color: Palette.ink,
  },
  optSub: {
    fontSize: 13,
    color: Palette.inkSoft,
    marginTop: 2,
    fontFamily: Fonts.sansMedium,
    lineHeight: 17,
  },
  badge: {
    alignSelf: 'flex-start',
    backgroundColor: Palette.honeySoft,
    paddingHorizontal: 7,
    paddingVertical: 3,
    borderRadius: 6,
    marginBottom: 4,
  },
  badgeText: {
    fontSize: 11,
    fontFamily: Fonts.sansBold,
    letterSpacing: 0.6,
    color: Palette.honeyDeep,
  },
  optCheck: {
    width: 24,
    height: 24,
    borderRadius: 12,
    borderWidth: 2,
    borderColor: Palette.sandDeep,
    alignItems: 'center',
    justifyContent: 'center',
  },
  optCheckSel: {
    backgroundColor: Palette.forest,
    borderColor: Palette.forest,
  },

  // Tiles (multi-select grid)
  tile: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 9,
    backgroundColor: Palette.card,
    borderWidth: 1.5,
    borderColor: Palette.line,
    borderRadius: 14,
    padding: 11,
  },
  tileSel: {
    borderColor: Palette.forest,
    backgroundColor: '#EEF3EE',
  },
  tilePressed: {
    transform: [{ scale: 0.97 }],
  },
  tileIco: {
    width: 30,
    height: 30,
    borderRadius: 9,
    backgroundColor: '#EAF0EA',
    alignItems: 'center',
    justifyContent: 'center',
  },
  tileIcoSel: {
    backgroundColor: Palette.forest,
  },
  tileLabel: {
    fontSize: 13,
    fontFamily: Fonts.sansSemibold,
    color: Palette.ink,
    lineHeight: 15,
  },
  tileCount: {
    marginTop: 2,
    fontSize: 12,
    fontFamily: Fonts.sansBold,
    color: Palette.forest,
  },
  stepper: {
    flexDirection: 'row',
    gap: 4,
    marginLeft: 6,
  },
  stepBtn: {
    width: 22,
    height: 22,
    borderRadius: 11,
    backgroundColor: '#EAF0EA',
    alignItems: 'center',
    justifyContent: 'center',
  },
});
